import { cn } from '@/shared/lib/cn';
import type { ReactNode } from 'react';

/**
 * OrbitVisual — the concentric "signal orbit" of the landing page
 * (extraction §5/§23c). Built in markup (orbit-circle rings +
 * pulsing core + floating glass node pills) as DOM, not an
 * image. Decorative: `aria-hidden`. Motion (float/pulse) is gated by
 * `prefers-reduced-motion` in globals.css.
 */
export interface OrbitNode {
  label: string;
  /** Absolute-position utility classes, e.g. "top-[15%] left-[20%]". */
  position: string;
  /** Inline animation-delay, e.g. "1.5s". */
  delay: string;
}

export interface OrbitVisualProps {
  rings: number[];
  nodes: OrbitNode[];
  core: ReactNode;
  heightClass?: string;
  className?: string;
}

export function OrbitVisual({
  rings,
  nodes,
  core,
  heightClass = 'h-[500px]',
  className,
}: OrbitVisualProps): ReactNode {
  return (
    <div
      aria-hidden="true"
      className={cn('orbit-container relative flex items-center justify-center', heightClass, className)}
    >
      {rings.map((size, i) => (
        <div
          key={size}
          className="orbit-circle"
          style={{ width: `${size}px`, height: `${size}px`, opacity: 0.2 + i * 0.2 }}
        />
      ))}
      <div className="absolute inset-0 flex items-center justify-center">
        {core}
        {nodes.map((node) => (
          <div
            key={node.label}
            className={cn(
              'glass-card animate-float absolute rounded-full px-4 py-2',
              node.position,
            )}
            style={{ animationDelay: node.delay }}
          >
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary-fixed" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-secondary-fixed">
                {node.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OrbitVisual;
